/**
 * `AuditTrailPanel` (doc 03 §6.15): everything the audit log holds about one row, newest first,
 * embedded in the detail views (`GET /v1/admin/audit-events/entity/{type}/{id}`, backend 42 §4.3).
 *
 * The read is finance- and support-scoped on the server. A viewer without either sees the panel's
 * header and the scope it needs rather than nothing at all (doc 02 §5.2, disabled-not-hidden),
 * and the read is never sent: a panel that 403s on every open is noise in the server log.
 */
import { useNavigate } from '@tanstack/react-router';
import { DataTable } from './data';
import { EmptyState, ErrorState, LoadingState } from './feedback';
import { Section } from './kit';
import { Button } from './primitives';
import { useConsole } from '../app/console';
import { MODULE_BY_KEY } from '../app/modules';
import { forModuleJump } from '../app/urlState';
import { errorMessage } from '../lib/api/client';
import { useEntityHistory, type AuditEventRow } from '../lib/api/hooks/audit';
import { can, describeNeed, type Scope } from '../lib/rbac';
import { formatDateTime } from '../lib/datetime';

export const AUDIT_READ_SCOPES: readonly Scope[] = ['finance', 'support'];

/** The audit console's URL filter for one row — what a panel's "Open in the audit console" sets. */
export function auditEntitySearch(entityType: string, entityId: number): Record<string, string> {
  return { audit_etype: entityType, audit_eid: String(entityId) };
}

/** The rows of a trail, newest first — shared with the KYC case's own History read (R310). */
export function TrailTable({ items }: { items: readonly AuditEventRow[] }) {
  return (
    <DataTable
      density="compact"
      columns={[
        { key: 'when', label: 'When', mono: true },
        { key: 'what', label: 'What' },
        { key: 'who', label: 'By' },
        { key: 'why', label: 'Why' },
      ]}
      rows={items.map((e) => ({
        id: e.id,
        when: formatDateTime(e.created_at),
        what: e.action,
        who: e.actor_admin_id !== null && e.actor_admin_id !== undefined ? `admin #${e.actor_admin_id}` : e.actor_type,
        why: e.reason ?? '—',
      }))}
    />
  );
}

function Trail({ entityType, entityId }: { entityType: string; entityId: number }) {
  const history = useEntityHistory(entityType, entityId);
  if (history.isPending) return <LoadingState message="Reading the history…" />;
  if (history.isError) return <ErrorState title="Could not read the history" cause={errorMessage(history.error)} />;
  if (history.data.items.length === 0) {
    return <EmptyState inline title="Nothing recorded" body="Every change to this row will be listed here." />;
  }
  return <TrailTable items={history.data.items} />;
}

export function AuditTrailPanel({ entityType, entityId }: { entityType: string; entityId: number }) {
  const { scope } = useConsole();
  const navigate = useNavigate();
  const reads = can(scope, AUDIT_READ_SCOPES);
  return (
    <Section
      title="History"
      note="from the audit log · newest first"
      pad={false}
      actions={
        /* The console itself is superadmin's (job health, chain verification): finance and support
           read this slice here but would meet a locked screen there, so they get no door to it. */
        reads && can(scope, MODULE_BY_KEY.audit!.scopes) ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              navigate({
                to: '/platform/audit',
                search: (prev: Record<string, unknown>) => ({ ...forModuleJump(prev), ...auditEntitySearch(entityType, entityId) }),
              } as never)
            }
          >
            Open in the audit console
          </Button>
        ) : undefined
      }
    >
      {reads ? (
        <Trail entityType={entityType} entityId={entityId} />
      ) : (
        <EmptyState
          inline
          icon="lock"
          title={`Requires scope: ${describeNeed(AUDIT_READ_SCOPES)}`}
          body="The audit log is read by finance and support."
        />
      )}
    </Section>
  );
}
